import React, { JSX, useId, useState } from "react";
import styled, { css } from "styled-components";
import { Z } from "@ui/styles/zIndex";
import { Card } from "@ui/components/containers/card";
import { ErrorIcon, EyeClosedIcon, EyeIcon } from "@ui/components/icons";

export interface SimpleTextInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "onSubmit"> {
	onSubmit?: (value: string) => void;
	onChange: (value: string) => void;
	inputRef?: React.RefObject<HTMLInputElement | null>;
	icon?: JSX.Element;
	iconPosition?: "left" | "right";
	value: string;
	placeholder?: string;
	autoComplete?: string;
	autoFocus?: boolean;
	error?: string;
	type?: string;
	color?: "primary" | "secondary" | "default";
	renderSuggestions?: () => JSX.Element;
	multiline?: boolean;
	renderBefore?: (focused: boolean) => JSX.Element;
	list?: string;
	textLimit?: number;
	height?: number;
	isApplication?: boolean;
	disabled?: boolean;
	name?: string;
}

export const SimpleTextInput: React.FC<SimpleTextInputProps> = ({
	value,
	onSubmit,
	onChange,
	inputRef,
	placeholder,
	icon,
	iconPosition = "right",
	autoComplete,
	error,
	type,
	color = "default",
	renderSuggestions,
	multiline = false,
	renderBefore,
	autoFocus,
	list,
	textLimit,
	height = undefined,
	isApplication,
	disabled,
	id,
	name,
	"aria-describedby": ariaDescribedBy,
	"aria-invalid": ariaInvalid,
	"aria-required": ariaRequired,
	...htmlProps
}) => {
	const [focused, setFocused] = useState(false);
	const isPassword = type === "password";
	const [isPasswordVisible, setPasswordVisible] = useState(!isPassword);
	const errorId = useId();
	const checkError: () => boolean = () => {
		if (error) return true;
		return Boolean(textLimit && value.length > textLimit);
	};
	const hasError = checkError();
	const showError = Boolean(error) && !focused;
	const describedBy = [ariaDescribedBy, showError ? errorId : undefined].filter(Boolean).join(" ") || undefined;
	const invalid = ariaInvalid ?? (hasError ? true : undefined);
	const controlProps = {
		id,
		name,
		"aria-describedby": describedBy,
		"aria-invalid": invalid,
		"aria-required": ariaRequired,
	};

	return (
		<Container {...htmlProps}>
			<Wrapper
				className="input-wrapper"
				iconPosition={icon ? iconPosition : null}
				error={hasError}
				focused={focused}
				height={height}
				isApplication={isApplication}
			>
				{renderBefore?.(focused)}
				{multiline ? (
					<textarea
						{...controlProps}
						onFocus={() => setFocused(true)}
						onBlur={() => setFocused(false)}
						value={value}
						onChange={(e) => onChange(e.currentTarget.value)}
						placeholder={placeholder}
						autoComplete={autoComplete}
						autoFocus={autoFocus}
					/>
				) : (
					<>
						<input
							{...controlProps}
							ref={inputRef}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									onSubmit?.(value);
								}
							}}
							type={isPassword && isPasswordVisible ? "text" : type}
							onFocus={() => setFocused(true)}
							onBlur={() => setFocused(false)}
							value={value}
							onChange={(e) => onChange(e.currentTarget.value)}
							placeholder={placeholder}
							autoComplete={autoComplete}
							autoFocus={autoFocus}
							list={list}
							disabled={disabled}
						/>
						{isPassword && (
							<PasswordVisibilityToggle
								type="button"
								onClick={(e) => {
									e.preventDefault();
									setPasswordVisible(!isPasswordVisible);
								}}
							>
								{isPasswordVisible ? <EyeClosedIcon /> : <EyeIcon />}
							</PasswordVisibilityToggle>
						)}
					</>
				)}
				<IconWrapper iconColor={color}>{icon}</IconWrapper>
			</Wrapper>
			{focused && !!renderSuggestions && <Suggestions>{renderSuggestions()}</Suggestions>}
			{showError && (
				<Error id={errorId}>
					<ErrorIcon />
					<div>{error}</div>
				</Error>
			)}
			{textLimit && <SizeCounter isInvalid={value.length > textLimit}>{`${value.length}/${textLimit}`}</SizeCounter>}
		</Container>
	);
};

const SizeCounter = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isInvalid",
})<{ isInvalid: boolean }>`
	text-align: right;
	color: ${({ theme, isInvalid }) => (isInvalid ? theme.colors.red : theme.colors.textTertiary)};
	font-size: 0.8125rem;
`;

const Container = styled.div`
	position: relative;
`;

const Wrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => !["focused", "error", "iconPosition", "height", "isApplication"].includes(prop),
})<{
	focused: boolean;
	error: boolean;
	iconPosition: "left" | "right" | null;
	height?: number;
	isApplication?: boolean;
}>`
	display: flex;
	align-items: center;
	min-height: 48px;
	border-radius: 999px;
	transition:
		border-color 0.15s linear,
		background-color 0.15s linear;
	border: solid 1px
		${({ theme, focused, error }) => (error ? theme.colors.red : focused ? theme.colors.primary : "#e3e7ec")};
	background-color: ${({ focused, error, theme }) =>
		error ? theme.colors.lightRed : focused ? theme.colors.white : "#fbfcfd"};
	overflow: hidden;
	padding: 0 8px 0 18px;

	${({ iconPosition }) =>
		iconPosition &&
		(iconPosition === "right"
			? css`
					flex-direction: row;
					padding-right: 15px;
					input {
						margin-right: 5px;
					}
				`
			: css`
					flex-direction: row-reverse;
					padding-left: 15px;
				`)};

	input {
		flex: 1;
		width: 100%;
		height: 48px;
		padding: 0;
		border: none;
		outline: none;
		background: transparent;
		font-size: 15px;
		color: ${({ theme }) => theme.colors.textPrimary};
		${({ height }) => (height ? `max-height:${height}px;min-height:${height}px;` : undefined)}

		::placeholder {
			color: #8a94a4;
			font-size: 15px;
			opacity: 1;
		}

		&:disabled {
			color: #8a94a4;
			cursor: not-allowed;
		}
	}

	textarea {
		flex: 1;
		width: 100%;
		min-height: 96px;
		padding: 14px 0;
		font-size: 15px;
		line-height: 1.5;
		border: none;
		outline: none;
		background: transparent;
		resize: vertical;
		color: ${({ theme }) => theme.colors.textPrimary};

		::placeholder {
			color: #8a94a4;
			font-size: 15px;
			opacity: 1;
		}
	}

	&:has(textarea) {
		height: auto;
		min-height: 96px;
		border-radius: 20px;
		align-items: flex-start;
		padding: 0 18px;
	}
`;

const PasswordVisibilityToggle = styled.button`
	flex-shrink: 0;
	width: 38px;
	height: 38px;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: #8a94a4;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	&:hover {
		background: #e7f4ec;
		color: ${({ theme }) => theme.colors.primary};
	}

	svg {
		width: 20px;
		height: 20px;
	}
`;

const IconWrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "iconColor",
})<{ iconColor: "primary" | "secondary" | "default" }>`
	min-width: 18px;

	${({ iconColor, theme }) =>
		iconColor !== "default" &&
		css`
			> svg .main-path {
				fill: ${iconColor === "primary" ? theme.colors.primary : theme.colors.secondary};
			}
		`};
`;

const Error = styled(Card)`
	z-index: ${Z.above(Z.over)};
	position: absolute;
	top: 100%;
	left: 30px;
	transform: translateY(-15px);
	padding: 15px;
	@media (max-width: 900px) {
		padding: 10px;
	}
	display: flex;
	align-items: center;
	max-width: 200px;
	${({ theme }) => theme.textStyles.text};
	border-radius: 10px;

	svg {
		margin-right: 10px;
		flex-shrink: 0;
	}
`;

const Suggestions = styled.div`
	position: absolute;
	z-index: ${Z.above(Z.over)};
	bottom: -1px;
	left: 0;
	transform: translateY(100%);
`;
